import { styled } from '@mui/material/styles';
import { ChangeEvent, ReactNode, useState } from 'react';
import { QTab, QTabList, QContainer, QTabContext } from '@kw/quokka-ui';
import { AddDeviceOwnerButton } from './AddDeviceOwnerButton';
import { AddDeviceOwnerModal } from './AddDeviceOwnerModal';
import DeviceOwnersTable from './DeviceOwnersTable';
// eslint-disable-next-line import/extensions
import { DeviceOwnerStatus } from './enum/DeviceOwnerKeyMaps.enum';
import { BackgroundProgressBar } from '../BackgroundProgressBar/BackgroundProgressBar';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  status: (typeof DeviceOwnerStatus)[keyof typeof DeviceOwnerStatus];
}
function TabPanel({ status, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`device-owner-tabpanel-${index}`}
      aria-labelledby={`device-owner-tab-${index}`}
      {...other}
    >
      {value === index && <DeviceOwnersTable ownerStatus={status} />}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    'data-testid': `device-owner-tab-${index}`,
    'aria-controls': `device-owner-tabpanel-${index}`,
  };
}

export default function DeviceOwners() {
  const [showAddDeviceOwnerModal, setShowAddDeviceOwnerModal] = useState<boolean>(false);
  const [tab, setTab] = useState<number>(0);
  const [ownerStatus, setOwnerStatus] = useState<(typeof DeviceOwnerStatus)[keyof typeof DeviceOwnerStatus]>(DeviceOwnerStatus.ENROLLED);

  const handleTabChange = (_event: ChangeEvent<HTMLButtonElement>, newTab: number) => {
    setTab(newTab);
    if (newTab === 0) {
      setOwnerStatus(DeviceOwnerStatus.ENROLLED);
    }
    if (newTab === 1) {
      setOwnerStatus(DeviceOwnerStatus.PENDING);
    }
    if (newTab === 2) {
      setOwnerStatus(DeviceOwnerStatus.UNENROLLED);
    }
  };

  return (
    <QContainer
      sx={{
        maxWidth: '100% !important',
      }}
    >
      <BackgroundProgressBar />
      <QTabContext value={tab}>
        <QTabList value={tab} onChange={handleTabChange} aria-label="Enrolled/Pending/Unenrolled Device Owners tabs">
          <StyledQTab label="Enrolled" value={0} {...a11yProps(0)} />
          <StyledQTab label="Pending" value={1} {...a11yProps(1)} />
          <StyledQTab label="Unenrolled" value={2} {...a11yProps(2)} />
          <AddDeviceOwnerButton onClick={() => setShowAddDeviceOwnerModal(true)} />
          <AddDeviceOwnerModal
            isOpen={showAddDeviceOwnerModal}
            onClose={() => setShowAddDeviceOwnerModal(false)}
            setOwnerStatus={setOwnerStatus}
            setTab={setTab}
          />
        </QTabList>
      </QTabContext>
      <TabPanel value={tab} index={0} status={ownerStatus} />
      <TabPanel value={tab} index={1} status={ownerStatus} />
      <TabPanel value={tab} index={2} status={ownerStatus} />
    </QContainer>
  );
}

const StyledQTab = styled(QTab)`
  padding: 20px 40px;
  &.Mui-selected {
    color: #000;
    font-weight: 700;
  }
`;
